// Composant Timeline pour suivi de candidature
import React from 'react';
import styled from 'styled-components';
import { theme } from '@theme';
import {
  FiCheckCircle, FiClock, FiFileText, FiUserCheck,
  FiUsers, FiHome, FiXCircle, FiAlertCircle
} from 'react-icons/fi';

// Types
export interface TimelineEvent {
  id: string;
  status: 'completed' | 'current' | 'pending' | 'rejected';
  title: string;
  description: string;
  date?: string;
  icon: React.ReactNode;
}

interface ApplicationTimelineProps {
  events: TimelineEvent[];
  currentStatus: string;
}

// Styled Components
const TimelineContainer = styled.div`
  background: white;
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing.xl};
  box-shadow: ${theme.shadows.sm};
`;

const TimelineTitle = styled.h3`
  font-size: ${theme.typography.sizes.xl};
  font-weight: ${theme.typography.weights.semibold};
  color: ${theme.colors.text};
  margin: 0 0 ${theme.spacing.xl} 0;
`;

const Timeline = styled.div`
  position: relative;
  padding-left: ${theme.spacing['2xl']};

  &::before {
    content: '';
    position: absolute;
    left: 19px;
    top: 0;
    bottom: 0;
    width: 2px;
    background: ${theme.colors.surface};
  }
`;

const TimelineItem = styled.div<{ status: string }>`
  position: relative;
  padding-bottom: ${theme.spacing.xl};

  &:last-child {
    padding-bottom: 0;
  }

  &::before {
    content: '';
    position: absolute;
    left: -${theme.spacing['2xl']};
    top: 0;
    width: 40px;
    height: 40px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1;

    ${props => {
      switch (props.status) {
        case 'completed':
          return `
            background: ${theme.colors.primary};
            box-shadow: 0 0 0 4px ${theme.colors.primary}20;
          `;
        case 'current':
          return `
            background: ${theme.colors.accent};
            box-shadow: 0 0 0 4px ${theme.colors.accent}20;
            animation: pulse 2s infinite;
          `;
        case 'rejected':
          return `
            background: ${theme.colors.error};
            box-shadow: 0 0 0 4px ${theme.colors.error}20;
          `;
        default:
          return `
            background: ${theme.colors.surface};
            border: 2px solid ${theme.colors.text}20;
          `;
      }
    }}
  }

  @keyframes pulse {
    0%, 100% {
      box-shadow: 0 0 0 4px ${theme.colors.accent}20;
    }
    50% {
      box-shadow: 0 0 0 8px ${theme.colors.accent}40;
    }
  }
`;

const TimelineIcon = styled.div<{ status: string }>`
  position: absolute;
  left: -${theme.spacing['2xl']};
  top: 0;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 20px;
  z-index: 2;

  ${props => {
    switch (props.status) {
      case 'completed':
        return `
          background: ${theme.colors.primary};
          color: white;
        `;
      case 'current':
        return `
          background: ${theme.colors.accent};
          color: white;
        `;
      case 'rejected':
        return `
          background: ${theme.colors.error};
          color: white;
        `;
      default:
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.text}40;
        `;
    }
  }}
`;

const TimelineContent = styled.div`
  padding-left: ${theme.spacing.lg};
`;

const TimelineHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: ${theme.spacing.sm};
`;

const TimelineEventTitle = styled.h4<{ status: string }>`
  font-size: ${theme.typography.sizes.lg};
  font-weight: ${theme.typography.weights.semibold};
  margin: 0;

  ${props => {
    switch (props.status) {
      case 'completed':
        return `color: ${theme.colors.primary};`;
      case 'current':
        return `color: ${theme.colors.accent};`;
      case 'rejected':
        return `color: ${theme.colors.error};`;
      default:
        return `color: ${theme.colors.text}60;`;
    }
  }}
`;

const TimelineDate = styled.div<{ status: string }>`
  font-size: ${theme.typography.sizes.sm};
  color: ${props => props.status === 'pending' ? theme.colors.text + '40' : theme.colors.text + '80'};
  white-space: nowrap;
`;

const TimelineDescription = styled.p<{ status: string }>`
  font-size: ${theme.typography.sizes.base};
  color: ${props => props.status === 'pending' ? theme.colors.text + '60' : theme.colors.text + '90'};
  margin: 0;
  line-height: 1.6;
`;

const StatusBadge = styled.span<{ status: string }>`
  display: inline-flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.full};
  font-size: ${theme.typography.sizes.xs};
  font-weight: ${theme.typography.weights.semibold};
  margin-top: ${theme.spacing.sm};

  ${props => {
    switch (props.status) {
      case 'completed':
        return `
          background: ${theme.colors.primary}20;
          color: ${theme.colors.primary};
        `;
      case 'current':
        return `
          background: ${theme.colors.accent}20;
          color: ${theme.colors.accent};
        `;
      case 'rejected':
        return `
          background: ${theme.colors.error}20;
          color: ${theme.colors.error};
        `;
      default:
        return `
          background: ${theme.colors.surface};
          color: ${theme.colors.text}60;
        `;
    }
  }}
`;

// Default timeline for applications
export const getDefaultTimeline = (status: string): TimelineEvent[] => {
  const allEvents: TimelineEvent[] = [
    {
      id: '1',
      status: 'completed',
      title: 'Candidature soumise',
      description: 'Votre candidature a été enregistrée avec succès',
      date: '15/01/2024',
      icon: <FiFileText />
    },
    {
      id: '2',
      status: 'completed',
      title: 'Vérification des documents',
      description: 'Tous vos documents ont été vérifiés et validés',
      date: '18/01/2024',
      icon: <FiUserCheck />
    },
    {
      id: '3',
      status: 'current',
      title: 'Scoring automatique',
      description: 'Votre dossier est en cours d\'évaluation par notre système IA',
      date: '20/01/2024',
      icon: <FiCheckCircle />
    },
    {
      id: '4',
      status: 'pending',
      title: 'Commission d\'attribution',
      description: 'La commission étudiera votre candidature et prendra une décision finale',
      icon: <FiUsers />
    },
    {
      id: '5',
      status: 'pending',
      title: 'Décision finale',
      description: 'Vous recevrez la notification de la décision concernant votre candidature',
      icon: <FiHome />
    }
  ];

  // Adjust timeline based on current status
  if (status === 'en_attente') {
    return allEvents.map((event, index) => ({
      ...event,
      status: index === 0 ? 'completed' : index === 1 ? 'current' : 'pending'
    }));
  }

  if (status === 'en_verification') {
    return allEvents.map((event, index) => ({
      ...event,
      status: index === 0 ? 'completed' : index === 1 ? 'current' : 'pending'
    }));
  }

  if (status === 'preselectionne') {
    return allEvents.map((event, index) => ({
      ...event,
      status: index <= 2 ? 'completed' : index === 3 ? 'current' : 'pending'
    }));
  }

  if (status === 'en_commission') {
    return allEvents.map((event, index) => ({
      ...event,
      status: index <= 3 ? 'completed' : index === 4 ? 'current' : 'pending'
    }));
  }

  if (status === 'accepte') {
    return allEvents.map(event => ({
      ...event,
      status: 'completed'
    }));
  }

  if (status === 'rejete') {
    return [
      ...allEvents.slice(0, 2).map(event => ({ ...event, status: 'completed' as const })),
      {
        id: 'rejected',
        status: 'rejected' as const,
        title: 'Candidature rejetée',
        description: 'Votre candidature n\'a pas été retenue. Consultez les détails pour plus d\'informations.',
        date: '22/01/2024',
        icon: <FiXCircle />
      }
    ];
  }

  return allEvents;
};

export const ApplicationTimeline: React.FC<ApplicationTimelineProps> = ({
  events,
  currentStatus
}) => {
  const getStatusText = (status: string): string => {
    switch (status) {
      case 'completed': return 'Complété';
      case 'current': return 'En cours';
      case 'rejected': return 'Rejeté';
      default: return 'À venir';
    }
  };

  return (
    <TimelineContainer>
      <TimelineTitle>Suivi de votre candidature</TimelineTitle>
      <Timeline>
        {events.map((event, index) => (
          <TimelineItem key={event.id} status={event.status}>
            <TimelineIcon status={event.status}>
              {event.status === 'rejected' ? <FiXCircle /> :
               event.status === 'completed' ? <FiCheckCircle /> :
               event.status === 'current' ? <FiClock /> :
               event.icon}
            </TimelineIcon>
            <TimelineContent>
              <TimelineHeader>
                <TimelineEventTitle status={event.status}>
                  {event.title}
                </TimelineEventTitle>
                {event.date && (
                  <TimelineDate status={event.status}>
                    {event.date}
                  </TimelineDate>
                )}
              </TimelineHeader>
              <TimelineDescription status={event.status}>
                {event.description}
              </TimelineDescription>
              <StatusBadge status={event.status}>
                {event.status === 'current' && <FiAlertCircle />}
                {getStatusText(event.status)}
              </StatusBadge>
            </TimelineContent>
          </TimelineItem>
        ))}
      </Timeline>
    </TimelineContainer>
  );
};
